/**
 * Shared revision dropdown (one control, two surfaces)
 *
 * The single presentational dropdown behind BOTH revision controls:
 *   - Insights tab  → RevisionScopeSelect  ("All revisions" + per-revision scope)
 *   - Workflow tab  → RevisionSelector     (active + past-revision read-only view)
 *
 * Implemented on antd `Select` (the same control the adjacent "Report:" picker
 * uses) so we inherit virtualized scrolling — important for journeys with many
 * revisions — keyboard navigation, and combobox a11y for free, rather than
 * hand-rolling a Dropdown + option list. The design's "pill + rich two-line
 * options + section header" is expressed via `prefix`, `optionRender`, and
 * `popupRender`. The two surfaces stay thin adapters that map their domain
 * values onto this shared option model.
 *
 * Surface-specific behaviour is configured via props (optional `prefix`, `icon`,
 * `menuHeader`, `staticMode`, `staticTooltip`) — not branched inside.
 */

import React from 'react'
import { Select, Tooltip } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import { RevisionActiveTag } from './revision-active-tag'
import './revision-dropdown.scss'

/** One selectable row in the dropdown body. */
export interface RevisionDropdownOption {
    /** Stable key — 'all' for the all-revisions scope, otherwise String(revisionId). */
    key: string
    /** Primary line, e.g. "All revisions" / "v2 · Split test" / "Current revision". */
    primary: string
    /** Optional secondary line, e.g. a date range or "promoted '…'" note. */
    sub?: string
    /** Render the teal ACTIVE badge beside the primary line. */
    showActiveBadge?: boolean
}

export interface RevisionDropdownProps {
    options: RevisionDropdownOption[]
    /** Key of the currently selected option. */
    selectedKey: string
    /** Fires with the chosen option key. */
    onSelect: (key: string) => void
    /** Compact text shown in the trigger pill for the current selection. */
    triggerText: string
    /** Optional label prefix inside the pill (e.g. "Revision:"). */
    prefix?: string
    /** Optional leading icon inside the pill (e.g. <HistoryOutlined/>). */
    icon?: React.ReactNode
    /** Accessible label for the trigger. */
    ariaLabel?: string
    /** Optional uppercase header rendered above the option list. */
    menuHeader?: string
    /** Static, non-interactive mode (single revision): muted, no caret, no dropdown. */
    staticMode?: boolean
    /** Optional tooltip shown on hover in staticMode (explains why it's not interactive). */
    staticTooltip?: string
    /** Minimum width of the trigger pill. */
    minWidth?: number
    /** Class applied to the root wrapper (lets adapters keep their testable hooks). */
    rootClassName?: string
    'data-testid'?: string
}

export const RevisionDropdown: React.FC<RevisionDropdownProps> = ({
    options,
    selectedKey,
    onSelect,
    triggerText,
    prefix,
    icon,
    ariaLabel,
    menuHeader,
    staticMode = false,
    staticTooltip,
    minWidth,
    rootClassName,
    'data-testid': dataTestId,
}) => {
    // ── Static (single revision): a non-interactive muted pill, no listbox ──
    // Kept as a plain span (not a disabled Select) so it reads as static chrome —
    // muted, no caret — and never exposes combobox semantics. Stays rendered so the
    // header doesn't reflow once a second revision appears.
    if (staticMode) {
        const staticPill = (
            <span
                className="revision-dropdown-trigger revision-dropdown-trigger--static"
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    height: 32,
                    padding: '0 12px',
                    borderRadius: 2,
                    fontSize: 14,
                    userSelect: 'none',
                    cursor: 'default',
                    minWidth: minWidth || undefined,
                    border: '1px solid #d9d9d9',
                    background: 'transparent',
                    color: '#838f9a',
                }}
            >
                {icon}
                {prefix && <span style={{ color: '#626262', flex: 'none', opacity: 0.8 }}>{prefix}</span>}
                <span style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {triggerText}
                </span>
            </span>
        )
        return (
            <div className={rootClassName} data-testid={dataTestId}>
                {staticTooltip ? <Tooltip title={staticTooltip}>{staticPill}</Tooltip> : staticPill}
            </div>
        )
    }

    // ── Interactive: antd Select. Each option's `label` is its plain primary line
    // (what the trigger shows for the current selection — equals triggerText by
    // construction); the rich two-line + ACTIVE-badge + check layout is rendered
    // in the popup via optionRender. ──
    const optionByKey = new Map(options.map((o) => [o.key, o]))
    const selectOptions = options.map((o) => ({ value: o.key, label: o.primary }))

    const prefixNode =
        icon || prefix ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#626262' }}>
                {icon}
                {prefix && <span>{prefix}</span>}
            </span>
        ) : undefined

    return (
        <div className={rootClassName} data-testid={dataTestId}>
            <Select
                className="revision-dropdown-select"
                aria-label={ariaLabel}
                value={selectedKey}
                onChange={(key) => onSelect(String(key))}
                options={selectOptions}
                prefix={prefixNode}
                // Virtualized list (rc-virtual-list) — scrolls natively past this height,
                // so a journey with many revisions never runs off the page.
                listHeight={360}
                popupMatchSelectWidth={false}
                // Selected state is shown by our left-aligned check in optionRender; drop
                // antd's default right-side selected icon to match the design.
                menuItemSelectedIcon={null}
                style={{ minWidth: minWidth ?? 200 }}
                optionRender={(oriOption) => {
                    const opt = optionByKey.get(String(oriOption.value))
                    if (!opt) return oriOption.label
                    const isSelected = String(oriOption.value) === String(selectedKey)
                    return (
                        <div
                            className={`revision-dropdown-option${isSelected ? ' revision-dropdown-option--selected' : ''}`}
                            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '3px 0', minWidth: 280 }}
                        >
                            <span style={{ width: 16, flexShrink: 0, color: 'var(--color-secondary-100)', lineHeight: 1.5 }}>
                                {isSelected && <CheckOutlined style={{ fontSize: 13 }} />}
                            </span>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontWeight: isSelected ? 600 : 500 }}>{opt.primary}</span>
                                    {opt.showActiveBadge && <RevisionActiveTag />}
                                </span>
                                {opt.sub && <div style={{ fontSize: 12, color: '#626262', marginTop: 2 }}>{opt.sub}</div>}
                            </span>
                        </div>
                    )
                }}
                popupRender={(menu) => (
                    <>
                        {menuHeader && (
                            <div
                                style={{
                                    padding: '8px 12px 6px',
                                    borderBottom: '1px solid #f0f0f0',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: '#626262',
                                    letterSpacing: '0.03em',
                                    textTransform: 'uppercase',
                                }}
                            >
                                {menuHeader}
                            </div>
                        )}
                        {menu}
                    </>
                )}
            />
        </div>
    )
}
